import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Users, CheckCircle2, Layers } from 'lucide-react';
import {
  BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from 'recharts';
import { businessPublicService, businessDashboardService } from '../../../services/businessApi';
import '../../../styles/businessTheme.css';
import './BusinessDashboard.css';

const STATUS_COLORS = {
  done: 'var(--success)',
  blocked: 'var(--danger)',
  planned: 'var(--accent)',
  not_started: 'var(--border-hover)',
  not_applicable: 'var(--text-muted)',
};
const STATUS_LABELS = { done: 'Done', blocked: 'Blocked', planned: 'Planned', not_started: 'Not Started', not_applicable: 'N/A' };

const CATEGORY_COLORS = ['var(--accent)', 'var(--warning)', 'var(--success)', 'var(--danger)', 'var(--info)'];

const tooltipStyle = { background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)' };

export default function BusinessDashboard() {
  const [plants, setPlants] = useState([]);
  const [plantId, setPlantId] = useState('');
  const [funnel, setFunnel] = useState([]);
  const [readiness, setReadiness] = useState([]);
  const [volume, setVolume] = useState([]);
  const [needsAttention, setNeedsAttention] = useState([]);
  const [supplierCounts, setSupplierCounts] = useState({ perPlant: [], commonAcrossPlants: 0 });

  useEffect(() => {
    businessPublicService.listPlants().then(res => setPlants(res.data.plants));
    businessDashboardService.getSupplierCounts().then(res => setSupplierCounts(res.data));
  }, []);

  const load = useCallback(async () => {
    const params = plantId ? { plantId } : {};
    const [funnelRes, readinessRes, volumeRes, attentionRes] = await Promise.all([
      businessDashboardService.getOnboardingFunnel(params),
      businessDashboardService.getGoLiveReadiness(params),
      businessDashboardService.getTicketVolume(params),
      businessDashboardService.getNeedsAttention(params),
    ]);
    setFunnel(funnelRes.data.funnel);
    setReadiness(plantId ? (readinessRes.data.readiness ? [readinessRes.data.readiness] : []) : readinessRes.data.readiness);
    setVolume(volumeRes.data.volume);
    setNeedsAttention(attentionRes.data.tickets);
  }, [plantId]);

  useEffect(() => { load(); }, [load]);

  const funnelData = funnel.map(f => ({ name: f.stepName, ...f.counts }));

  const STAT_COLORS = ['blue', 'green', 'orange', 'purple'];

  return (
    <div className="business-dashboard-page biz-theme biz-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Business Support Dashboard</h1>
          <p className="page-subtitle">l-setu supplier onboarding &amp; ticket health</p>
        </div>
        <select value={plantId} onChange={e => setPlantId(e.target.value)}>
          <option value="">All plants</option>
          {plants.map(p => <option key={p._id} value={p._id}>{p.name} ({p.code})</option>)}
        </select>
      </div>

      <div className="biz-stat-grid">
        {supplierCounts.perPlant.map((p, i) => (
          <div key={p.plantId} className={`biz-stat-card biz-stat-card--${STAT_COLORS[i % STAT_COLORS.length]}`}>
            <div className="biz-stat-card__header"><Users size={14} /> Suppliers — {p.plantName}</div>
            <div className="biz-stat-card__value">{p.count}</div>
            <div className="biz-stat-card__sub">Mapped in this plant</div>
          </div>
        ))}
        <div className="biz-stat-card biz-stat-card--red">
          <div className="biz-stat-card__header"><Layers size={14} /> Common Across Plants</div>
          <div className="biz-stat-card__value">{supplierCounts.commonAcrossPlants}</div>
          <div className="biz-stat-card__sub">Suppliers active at 2+ plants</div>
        </div>
      </div>

      <div className="card biz-chart-card">
        <div className="biz-chart-card__header"><span>Go-Live Readiness</span></div>
        <div className="readiness-grid">
          {readiness.map(r => (
            <div key={r.plantId} className="readiness-card">
              <div className="readiness-card__title">{r.plantName}</div>
              <div className="readiness-card__stat">
                <CheckCircle2 size={16} color="var(--success)" />
                <span>{r.fullyOnboarded} / {r.totalMapped} fully onboarded</span>
              </div>
              {r.blockedBy.length > 0 && (
                <div className="readiness-blockers">
                  {r.blockedBy.map(b => (
                    <span key={b.stepId} className="readiness-blocker-chip">{b.stepName}: {b.count}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
          {readiness.length === 0 && <div className="empty-state">No mapped suppliers yet</div>}
        </div>
      </div>

      <div className="card biz-chart-card">
        <div className="biz-chart-card__header"><span>Onboarding Funnel — Status by Step</span></div>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={funnelData} layout="vertical" margin={{ left: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
            <XAxis type="number" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" width={180} tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend formatter={v => STATUS_LABELS[v] || v} wrapperStyle={{ fontSize: 12 }} />
            {Object.keys(STATUS_COLORS).map(status => (
              <Bar key={status} dataKey={status} stackId="a" fill={STATUS_COLORS[status]} name={status} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="biz-dashboard-charts">
        <div className="card biz-chart-card">
          <div className="biz-chart-card__header"><span>Ticket Volume by Category</span></div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={volume}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="categoryName" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {volume.map((_, i) => <Cell key={i} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card biz-chart-card">
          <div className="biz-chart-card__header">
            <AlertTriangle size={16} color="var(--warning)" />
            <span>Needs Attention — Stale Tickets</span>
          </div>
          <div className="needs-attention-list">
            {needsAttention.map(t => (
              <Link key={t._id} to={`/business/tickets/${t._id}`} className="needs-attention-row">
                <div>
                  <div className="bt-number">{t.ticketNumber}</div>
                  <div className="bt-title">{t.title}</div>
                </div>
                <div className="needs-attention-meta">
                  <span>{t.plant?.name}</span>
                  <span>{new Date(t.lastActivityAt).toLocaleDateString()}</span>
                </div>
              </Link>
            ))}
            {needsAttention.length === 0 && <div className="empty-state">Nothing stale — good shape</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
